import { type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  DenseTable,
  StatusBadge,
  estimateSuffix,
  type Column,
  type Measure,
  type StatusLevel,
} from "../../components/index.ts";
import type { ApiError } from "../../lib/api.ts";
import type { components } from "@oathera/contract/api";
import type { AgentRisk, AgentRiskComponent } from "./data.ts";
import { DrawerSection } from "./DetailDrawer.tsx";

type Quantity = components["schemas"]["Quantity"];
type ComponentName = AgentRiskComponent["name"];

/**
 * The agent-risk section of the Agents & Apps drawer (task 7.5; requirement 13.5, 13.6). It renders
 * the per-agent weighted risk score and its component breakdown from `GET
 * /api/v1/agents/{agent_sub}/risk` (operationId `getAgentRisk`, wired in task 4.6) so an operator
 * can see not just the 0–100 number but exactly how deny rate, unenforced calls, tool errors,
 * first-use of tools, open critical/high findings and exceptions each push it up.
 *
 * It owns no API shapes — those come from `@oathera/contract/api` via `data.ts` — and no stub data
 * (requirement 13.6). It presents every state honestly (design 12.3): loading while the read is in
 * flight, a plain message on error (a 404 for an agent with no risk data reads as an honest empty
 * state, not a crash), and the score + components when the read resolves. A `Quantity` with no data
 * is shown as an em dash and never as a reassuring exact figure.
 */

/* ---- Quantity → measure / value (design 12.3), shared with the sibling screens -------------- */

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity | undefined | null): Measure {
  if (q === undefined || q === null || q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** The numeric value of a `Quantity`, or `null` when it carries no data. */
export function quantityValue(q: Quantity | undefined | null): number | null {
  return q === undefined || q === null ? null : q.value;
}

/** Format a `Quantity` as a bounded two-decimal number, with the honest em dash for no data. */
export function formatQuantity(q: Quantity | undefined | null): string {
  const v = quantityValue(q);
  if (v === null) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/* ---- Risk-component vocabulary -------------------------------------------------------------- */

/** Human label for each weighted risk component (the closed `AgentRiskComponent.name` set). */
export function componentLabel(name: ComponentName): string {
  switch (name) {
    case "deny_rate":
      return "Deny rate";
    case "unenforced_calls":
      return "Unenforced calls";
    case "tool_errors":
      return "Tool errors";
    case "first_use_tools":
      return "First-use tools";
    case "open_critical_high":
      return "Open critical / high findings";
    case "exceptions":
      return "Exceptions";
    default:
      return name;
  }
}

/** Format a 0..1 weight as a percentage share of the score, with an em dash for a missing weight. */
export function formatWeight(weight: number): string {
  if (!Number.isFinite(weight)) return "—";
  return `${(weight * 100).toFixed(0)}%`;
}

/**
 * The status level for a 0–100 risk score (design 12.4: by icon + text, never colour alone). Higher
 * risk is more severe: ≥ 67 is an error, ≥ 34 a warning, below that neutral. A missing score is
 * grey `unknown` and never a reassuring green (design 12.3).
 */
export function scoreLevel(score: number | null): StatusLevel {
  if (score === null) return "unknown";
  if (score >= 67) return "error";
  if (score >= 34) return "warn";
  return "neutral";
}

/** A short word for the score band, matching {@link scoreLevel}. */
export function scoreBand(score: number | null): string {
  if (score === null) return "No reading";
  if (score >= 67) return "High risk";
  if (score >= 34) return "Elevated";
  return "Low risk";
}

/* ---- The panel ------------------------------------------------------------------------------ */

export function AgentRiskPanel(props: {
  readonly query: UseQueryResult<AgentRisk, ApiError>;
}): ReactNode {
  const { data, isError, error, isPending } = props.query;

  let body: ReactNode;
  if (isPending) {
    body = (
      <p className="adna-risk__state" role="status" aria-live="polite">
        Loading the risk score…
      </p>
    );
  } else if (isError) {
    body = riskErrorBody(error);
  } else if (data !== undefined) {
    body = <RiskBody risk={data} />;
  } else {
    body = null;
  }

  return <DrawerSection title="Risk score">{body}</DrawerSection>;
}

/**
 * The error body. A `not_found` means the agent simply has no risk data in range — an honest empty
 * state, not a failure — so it reads as a plain note rather than an alarm.
 */
function riskErrorBody(error: ApiError | null): ReactNode {
  if (error !== null && error.kind === "not_found") {
    return (
      <p className="adna-risk__state" data-kind="empty">
        No risk score has been computed for this agent yet.
      </p>
    );
  }
  return (
    <p className="adna-risk__state" data-kind="error">
      {riskErrorMessage(error)}
    </p>
  );
}

function riskErrorMessage(error: ApiError | null): string {
  if (error === null) return "The risk score could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see this agent's risk. The server enforces access.";
    case "network":
      return "The risk request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

/** The score headline plus the component breakdown. */
function RiskBody(props: { risk: AgentRisk }): ReactNode {
  const { risk } = props;
  const score = quantityValue(risk.score);
  const measure = quantityMeasure(risk.score);
  // Clamp the bar fill to 0–100 so an out-of-range figure never overflows the track; the number
  // itself is still shown verbatim so nothing is silently hidden.
  const fill = score === null ? 0 : Math.max(0, Math.min(100, score));

  return (
    <div className="adna-risk">
      <div className="drawer-score">
        <span className="drawer-score__value" data-measure={measure}>
          {formatQuantity(risk.score)}
          {estimateSuffix(measure)}
        </span>
        <div className="drawer-score__bar" aria-hidden="true">
          <span className="drawer-score__fill" style={{ width: `${fill}%` }} />
        </div>
      </div>
      <p className="drawer-score__note">
        <StatusBadge level={scoreLevel(score)} label={scoreBand(score)} size="sm" /> · weighted
        score out of 100
      </p>
      <RiskComponents components={risk.components} />
    </div>
  );
}

/**
 * The component breakdown table: each weighted component with its raw value, its weight (share of
 * the score) and its contribution, sorted by contribution so the components driving the score sit at
 * the top. Every row is a `Quantity`-aware cell, so a component the server could not measure shows an
 * em dash rather than a fabricated zero.
 */
function RiskComponents(props: { components: readonly AgentRiskComponent[] }): ReactNode {
  const rows = [...props.components].sort(
    (a, b) => (quantityValue(b.contribution) ?? 0) - (quantityValue(a.contribution) ?? 0),
  );

  const columns: readonly Column<AgentRiskComponent>[] = [
    {
      key: "name",
      header: "Component",
      isRowHeader: true,
      cell: (r) => componentLabel(r.name),
    },
    {
      key: "raw",
      header: "Raw",
      align: "right",
      cell: (r) => <span data-measure={quantityMeasure(r.raw)}>{formatQuantity(r.raw)}</span>,
    },
    {
      key: "weight",
      header: "Weight",
      align: "right",
      cell: (r) => formatWeight(r.weight),
    },
    {
      key: "contribution",
      header: "Contribution",
      align: "right",
      cell: (r) => (
        <span data-measure={quantityMeasure(r.contribution)}>
          {formatQuantity(r.contribution)}
          {estimateSuffix(quantityMeasure(r.contribution))}
        </span>
      ),
    },
  ];

  return (
    <DenseTable
      caption="Risk score components with raw value, weight and contribution"
      captionHidden
      columns={columns}
      rows={rows}
      rowKey={(r) => r.name}
      emptyMessage="This score has no component breakdown."
    />
  );
}
